import { useStytchSession } from '@stytch/nextjs';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { AuthRoutes, CommonRoutes, isAlwaysPublicRoute, ReferralRoutes } from 'routes';
import { useUser } from 'context/user';
import LoadingScreen from './LoadingScreen';
import { cancelAxiosRequests } from 'services/config';
import { checkIfSuperAdminMode, checkIfSupportAdminMode, isValidRoute, THERA_SkipEmailVerify } from 'utils/user';
import MaintenanceIcon from 'icons/MaintenanceIcon';
import { isRecentRoute } from 'utils/app';

const PRELOGIN_ROUTES = [
  '/',
  AuthRoutes.SuperAdmin,
  AuthRoutes.SignIn,
  AuthRoutes.SignUp,
  AuthRoutes.ResetPassword,
  AuthRoutes.ActionOnQuoteForCustomerRoute,
  AuthRoutes.TheraStart,
  AuthRoutes.TheraStartSuccess,
  AuthRoutes.TheraStartCancel,
  ReferralRoutes.Link,
  ReferralRoutes.SignIn,
  AuthRoutes.TheraComponents,
];
const ErrorRoutes = ['/404', '/500'];
export const initialTheraRoute_LS_key = 'initialTheraRoute';
export const initialTheraRouteTimestamp_LS_key = 'initialTheraRouteTimestamp';

export default function AuthRoutingHandler({ children }) {
  const [showChildren, setShowChildren] = useState(false);
  const { session, isInitialized } = useStytchSession();
  const { user, isLoggingOut, roleType, showAccountSwitcher, hasNoAffiliations } = useUser();
  const { pathname, asPath: initialRoute, replace, query } = useRouter();
  const isPreloginRoute = PRELOGIN_ROUTES.includes(pathname);
  const isSuperAdminRoute = AuthRoutes.SuperAdmin.includes(pathname);
  const featureFlagEnabled = useFeatureFlagEnabled('maintenance-mode');
  const stytch_token_type = query?.stytch_token_type?.toString();
  const token = query?.token?.toString();
  const isAuthenticating = pathname === AuthRoutes.Authenticate && token && stytch_token_type ? true : false;

  // Admin mode: if custom claim exists on session, use it exclusively (prevents localStorage forgery).
  // With no claim, the checkIf* helpers honor localStorage in non-prod Stytch environments only.
  const sessionAdminType = session?.custom_claims?.admin_type as string | undefined;
  const { isSuperAdminEnabled, isSuperAdminModeReady } = useMemo(() => {
    if (typeof sessionAdminType === 'string') {
      return { isSuperAdminEnabled: sessionAdminType === 'super', isSuperAdminModeReady: sessionAdminType === 'super' };
    }
    if (typeof window !== 'undefined') {
      const isSAEnabled = checkIfSuperAdminMode();
      const isSAReady = isSAEnabled && Boolean(window.localStorage.getItem(THERA_SkipEmailVerify));
      return { isSuperAdminEnabled: isSAEnabled, isSuperAdminModeReady: isSAReady };
    }
    return { isSuperAdminEnabled: false, isSuperAdminModeReady: false };
  }, [sessionAdminType]);

  const isSupportAdminEnabled = useMemo(() => {
    if (typeof sessionAdminType === 'string') return sessionAdminType === 'support';
    return checkIfSupportAdminMode();
  }, [sessionAdminType]);

  const handleAuthenticatedRouting = useCallback(async () => {
    if (!session || !user) return;
    if (isSuperAdminRoute) return;
    // Check email verification first but allow superadmin mode
    if (!user.isEmailVerified && !isSuperAdminModeReady) {
      // Don't redirect to dashboard if email isn't verified
      return;
    }
    // if user is authenticating, don't redirect to dashboard
    if (isAuthenticating) {
      return;
    }

    // manager or worker with onboarding done
    if (roleType === 'manager' || user.isOnboarded) {
      if (pathname === ReferralRoutes.SignIn) {
        await replace(ReferralRoutes.Portal);
        return;
      }
      if (pathname === ReferralRoutes.Portal) {
        return;
      }

      const initialRoute = localStorage.getItem(initialTheraRoute_LS_key);
      const initialRouteTimestamp = localStorage.getItem(initialTheraRouteTimestamp_LS_key);

      if (initialRoute && isRecentRoute(initialRouteTimestamp) && isValidRoute(initialRoute, roleType)) {
        localStorage.removeItem(initialTheraRoute_LS_key);
        localStorage.removeItem(initialTheraRouteTimestamp_LS_key);
        await replace(initialRoute);
      } else {
        await replace(CommonRoutes.Dashboard);
      }
    }
  }, [session, user, roleType, isSuperAdminRoute, replace, isSuperAdminModeReady, isAuthenticating, pathname]);

  useEffect(() => {
    if (!isInitialized) return;

    // 1. Reset password is special-cased because we want to allow users to reset their password even if they're not logged in
    if (pathname === AuthRoutes.ResetPassword) {
      setShowChildren(true);
      return;
    }

    if (isAlwaysPublicRoute(pathname)) {
      setShowChildren(true);
      return;
    }

    // 2. Authenticate is special-cased because this is where we transition from not having a session to having one
    if (pathname === AuthRoutes.Authenticate) {
      setShowChildren(true);
      handleAuthenticatedRouting();
      return;
    }

    // 3. If route is prelogin
    if (isPreloginRoute) {
      if (session) handleAuthenticatedRouting();
      setShowChildren(true);
      return;
    }

    // 4. If no session, capture initial route, except for error routes
    if (!session) {
      // Not logged in but on a protected route
      // capture initial route (unless it's an error route or user is logging out)
      if (!ErrorRoutes.includes(pathname) && !isLoggingOut) {
        localStorage.setItem(initialTheraRoute_LS_key, initialRoute);
        localStorage.setItem(initialTheraRouteTimestamp_LS_key, Date.now().toString());
      }
      cancelAxiosRequests();
      // Redirect referral portal pages to the referral-specific sign-in
      if (pathname.startsWith('/referral')) {
        replace(AuthRoutes.SignIn);
      } else {
        replace(AuthRoutes.SignIn);
      }
    } else {
      // 5. If session, check if referral-only user
      if (hasNoAffiliations) {
        const isReferralRoute = pathname.startsWith('/referral') || pathname.startsWith('/ref');
        if (!isReferralRoute) {
          replace(ReferralRoutes.Portal);
          return;
        }
      }

      // 6. Show children
      setShowChildren(true);
      // don't remove initial route if account switcher will be shown on logging in,
      // removing initial route causes the user to not get redirected to the intended page.
      // unresolved roleType means selection is still pending (switcher stays off until MFA is done)
      if (showAccountSwitcher || !roleType) return;
      // remove initial route from local storage
      localStorage.removeItem(initialTheraRoute_LS_key);
      localStorage.removeItem(initialTheraRouteTimestamp_LS_key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, isLoggingOut, handleAuthenticatedRouting, isPreloginRoute, session]);

  const goToSuperAdminPage = useCallback(() => {
    replace(AuthRoutes.SuperAdmin);
  }, [replace]);

  if (!isInitialized || !showChildren) return <LoadingScreen />;

  return (
    <>
      {isSuperAdminEnabled && (
        <>
          <div
            id="super-admin-mode-top-bar"
            onClick={goToSuperAdminPage}
            className={`fixed left-0 top-0 !z-[999999] !h-2 !min-h-0 w-full cursor-pointer ${
              isSuperAdminModeReady ? 'bg-therablue' : 'bg-orange-300'
            }`}
          />
          {featureFlagEnabled && isSuperAdminModeReady && (
            <div className="fixed top-14 z-[9999] flex !h-5 !min-h-0 w-full cursor-pointer items-center justify-center bg-slate-300 text-sm">
              <MaintenanceIcon className="mr-3 h-3 w-3" /> Maintenance mode is on for some or all users{' '}
              <MaintenanceIcon className="ml-3 h-3 w-3" />
            </div>
          )}
        </>
      )}
      {isSupportAdminEnabled && !isSuperAdminEnabled && (
        <div
          id="support-admin-mode-top-bar"
          onClick={goToSuperAdminPage}
          className="fixed left-0 top-0 !z-[999999] !h-2 !min-h-0 w-full cursor-pointer bg-green-500"
        />
      )}
      {children}
    </>
  );
}
